import { defineAsyncComponent } from '../../../lib/utils/vue3compat/vue.js?vue3';
import { GlButtonGroup, GlDisclosureDropdown, GlDisclosureDropdownItem } from '../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { getAutoSaveKeyFromDiscussion } from '~/lib/utils/autosave';
import { isLoggedIn } from '~/lib/utils/common_utils';
import { confirmAction } from '../../../lib/utils/confirm_via_gl_modal/confirm_via_gl_modal.js?vue3';
import { ignoreWhilePending } from '~/lib/utils/ignore_while_pending';
import { suppressShortcutsUntilInputFocus } from '~/lib/mousetrap';
import { CopyAsGFM } from '../../../behaviors/markdown/copy_as_gfm.js?vue3';
import { s__, __, sprintf } from '~/locale';
import { detectAndConfirmSensitiveTokens } from '../../../lib/utils/secret_detection.js?vue3';
import { createAlert } from '../../../alert.js?vue3';
import DiscussionReplyPlaceholder from '../../../notes/components/discussion_reply_placeholder.vue?vue3';
import ResolveDiscussionButton from '../../../notes/components/resolve_discussion_button.vue?vue3';
import ResolveWithIssueButton from '../../../notes/components/discussion_resolve_with_issue_button.vue?vue3';
import { glSlotsMixin } from '~/lib/utils/vue3compat/gl_slots_mixin';
import NoteSignedOutWidget from './note_signed_out_widget.vue?vue3';
import NoteForm from './note_form.vue?vue3';
import DiscussionNotes from './discussion_notes.vue?vue3';
export default {
    name: 'NoteableDiscussion',
    components: {
        GlButtonGroup,
        DiscussionReplyPlaceholder,
        ResolveDiscussionButton,
        NoteSignedOutWidget,
        NoteForm,
        DiscussionNotes,
        ResolveWithIssueButton,
        GlDisclosureDropdown,
        GlDisclosureDropdownItem,
        ResolveWithDuoDropdownItem: defineAsyncComponent(()=>import('../../../../../../ee/app/assets/javascripts/notes/components/resolve_with_duo_dropdown_item.vue?vue3'))
    },
    mixins: [
        glSlotsMixin
    ],
    inject: {
        store: {
            type: Object
        },
        userPermissions: {
            type: Object
        },
        sourceBranch: {
            default: ''
        },
        iid: {
            default: null
        },
        resolveCodeSuggestions: {
            default: null
        }
    },
    provide () {
        return {
            codeSuggestions: ()=>this.resolveCodeSuggestions?.(this.discussion)
        };
    },
    props: {
        discussion: {
            type: Object,
            required: true
        },
        requestLastNoteEditing: {
            type: Function,
            required: true
        },
        toggleResolveNote: {
            type: Function,
            required: false,
            default: null
        },
        timelineLayout: {
            type: Boolean,
            required: false,
            default: false
        },
        isLastDiscussion: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    emits: [
        'cancel-editing',
        'note-edited',
        'start-editing',
        'start-replying',
        'stop-replying',
        'toggle-discussion-replies'
    ],
    data () {
        return {
            isLoggedIn: isLoggedIn(),
            isResolving: false,
            isDuoLoading: false
        };
    },
    computed: {
        commentType () {
            return this.discussion.internal ? __('internal note') : __('comment');
        },
        autosaveKey () {
            return getAutoSaveKeyFromDiscussion(this.discussion);
        },
        saveButtonTitle () {
            return this.discussion.internal ? __('Reply internally') : __('Reply');
        },
        canReply () {
            return !this.discussion.isDraft && !this.discussion.notes[0]?.system && !this.discussion.individual_note;
        },
        resolvable () {
            return this.discussion.resolvable;
        },
        canResolve () {
            return this.discussion.notes.filter((note)=>note.resolvable).every((note)=>note.current_user?.can_resolve_discussion);
        },
        resolveButtonTitle () {
            return this.discussion.resolved ? __('Reopen thread') : __('Resolve thread');
        },
        hasDraftReply () {
            return this.discussion.notes.some((note)=>note.isDraft);
        },
        canStartReview () {
            return Boolean(this.store.addDraftToDiscussion) && !this.hasDraftReply;
        },
        resolveWithIssuePath () {
            return !this.discussion.resolved ? this.discussion.resolve_with_issue_path : '';
        },
        showResolveDiscussionToggle () {
            return Boolean(this.toggleResolveNote) && this.resolvable && this.canResolve;
        },
        canResolveDiscussionsWithAi () {
            return Boolean(this.userPermissions.can_resolve_discussions_with_ai);
        },
        showSecondaryActionsDropdown () {
            return this.discussion.resolvable && !this.discussion.resolved && this.canResolveDiscussionsWithAi;
        },
        showIssueButton () {
            return this.discussion.resolvable && !this.discussion.resolved && Boolean(this.resolveWithIssuePath);
        },
        resolveWithIssueItem () {
            return {
                text: __('Resolve with new issue'),
                href: this.resolveWithIssuePath
            };
        }
    },
    methods: {
        async toggleResolve () {
            this.isResolving = true;
            try {
                await this.toggleResolveNote(this.discussion);
            } catch (error) {
                createAlert({
                    message: __('Something went wrong while resolving this discussion. Please try again.'),
                    error,
                    captureError: true,
                    parent: this.$el
                });
            } finally{
                this.isResolving = false;
            }
        },
        showReplyForm (text) {
            suppressShortcutsUntilInputFocus();
            this.$emit('start-replying');
            if (typeof text !== 'undefined') {
                this.$nextTick(()=>{
                    this.$refs.noteForm.append(text);
                });
            }
        },
        async onQuoteReply () {
            const text = await CopyAsGFM.selectionToGfm();
            // prevent the hotkey keystroke from landing in the reply form
            requestAnimationFrame(()=>{
                this.showReplyForm(text);
            });
        },
        cancelReplyForm: ignoreWhilePending(async function cancelReplyForm(shouldConfirm, isDirty) {
            if (shouldConfirm && isDirty) {
                const msg = sprintf(s__('Notes|Are you sure you want to cancel creating this %{commentType}?'), {
                    commentType: this.commentType
                });
                const confirmed = await confirmAction(msg, {
                    primaryBtnText: __('Discard changes'),
                    cancelBtnText: __('Continue editing')
                });
                if (!confirmed) {
                    return;
                }
            }
            this.$emit('stop-replying');
        }),
        async saveNote (noteText, shouldResolve) {
            if (!noteText) {
                this.cancelReplyForm();
                return;
            }
            const confirmSubmit = await detectAndConfirmSensitiveTokens({
                content: noteText
            });
            if (!confirmSubmit) {
                return;
            }
            await this.store.replyToDiscussion(this.discussion, noteText, shouldResolve);
            this.$emit('stop-replying');
        },
        async saveDraft (noteText, shouldResolve) {
            if (!noteText) {
                this.cancelReplyForm();
                return;
            }
            const confirmSubmit = await detectAndConfirmSensitiveTokens({
                content: noteText
            });
            if (!confirmSubmit) {
                return;
            }
            await this.store.addDraftToDiscussion(this.discussion, noteText, shouldResolve);
            this.$emit('stop-replying');
        }
    }
};
